/** ИИ-контент — агент карточек товаров по остаткам из 1С (Далион Тренд).
 *
 * Флоу: загрузить CSV/XLSX остатков → превью с автодетектом колонок
 * (название / остаток / себестоимость) → запустить прогон. Агент
 * сопоставляет позиции с каталогом, для новых ищет информацию в интернете
 * (Claude + web search) и создаёт карточки-ЧЕРНОВИКИ (is_active=false).
 * Прогон идёт в фоне; история ниже обновляется, пока что-то бежит.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bot,
  FileSpreadsheet,
  Loader2,
  Play,
  Save,
  XCircle,
} from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export interface AiRun {
  id: number;
  status: "pending" | "running" | "success" | "partial" | "failed";
  model: string;
  file_name: string | null;
  stats: Record<string, number>;
  error_log: {
    timeout?: boolean;
    exception?: string;
    cancelled_manually?: boolean;
    cost_cap_hit?: boolean;
  };
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export const RUN_STATUS_LABEL: Record<AiRun["status"], string> = {
  pending: "В очереди",
  running: "Выполняется",
  success: "Готово",
  partial: "Готово частично",
  failed: "Ошибка",
};

export const RUN_STATUS_VARIANT: Record<
  AiRun["status"],
  "success" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  running: "secondary",
  success: "success",
  partial: "destructive",
  failed: "destructive",
};

interface ParsePreview {
  headers: string[];
  rows: string[][];
  total_rows: number;
  name_column: number | null;
  qty_column: number | null;
  cost_column: number | null;
  code_column: number | null;
}

interface ModelOption {
  id: string;
  label: string;
  price_hint: string;
  is_default: boolean;
}

interface AdminSettings {
  anthropic_api_key_set?: boolean;
  ai_content_model?: string;
  ai_content_cost_cap_usd?: number;
  ai_content_default_discount_percent?: number;
  ai_content_concurrency?: number;
  ai_content_markup_percent?: number;
}

const NONE_COLUMN = "__none__";

export function AiContentPage(): JSX.Element {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ParsePreview | null>(null);
  const [nameCol, setNameCol] = useState<string>(NONE_COLUMN);
  const [qtyCol, setQtyCol] = useState<string>(NONE_COLUMN);
  const [costCol, setCostCol] = useState<string>(NONE_COLUMN);
  const [codeCol, setCodeCol] = useState<string>(NONE_COLUMN);
  // Пусто = весь файл. Раньше стояло 50: прогон вставал после партии и
  // ждал ручного «Продолжить» — на файле в 1375 позиций это давало сутки
  // простоя. Теперь прогон идёт до конца сам, а поле остаётся для
  // тестовых запусков на десятке позиций.
  const [batchSize, setBatchSize] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => (await api.get<AdminSettings>("/settings")).data,
  });

  const { data: models } = useQuery({
    queryKey: ["ai-content-models"],
    queryFn: async () => (await api.get<ModelOption[]>("/ai-content/models")).data,
    staleTime: 60 * 60 * 1000,
  });

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ["ai-content-runs"],
    queryFn: async () => (await api.get<AiRun[]>("/ai-content/runs")).data,
    refetchInterval: (query) => {
      const data = query.state.data as AiRun[] | undefined;
      const active = data?.some((r) => r.status === "pending" || r.status === "running");
      return active ? 3000 : false;
    },
  });

  const saveModelMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      await api.patch("/settings", patch);
    },
    onSuccess: () => {
      toast.success("Настройки сохранены");
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Ошибка сохранения"),
  });

  const parseMutation = useMutation({
    mutationFn: async (f: File) => {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post<ParsePreview>("/ai-content/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (data) => {
      setPreview(data);
      setNameCol(data.name_column !== null ? String(data.name_column) : NONE_COLUMN);
      setQtyCol(data.qty_column !== null ? String(data.qty_column) : NONE_COLUMN);
      setCostCol(data.cost_column !== null ? String(data.cost_column) : NONE_COLUMN);
      setCodeCol(data.code_column !== null ? String(data.code_column) : NONE_COLUMN);
      if (data.name_column === null) {
        toast.warning("Не удалось определить колонку с названием — выберите вручную");
      }
    },
    onError: (e: any) => {
      setPreview(null);
      toast.error(e?.response?.data?.error?.message ?? "Не удалось разобрать файл");
    },
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Файл не выбран");
      const form = new FormData();
      form.append("file", file);
      form.append("name_column", nameCol);
      form.append("qty_column", qtyCol);
      if (costCol !== NONE_COLUMN) form.append("cost_column", costCol);
      if (codeCol !== NONE_COLUMN) form.append("code_column", codeCol);
      const batch = parseInt(batchSize, 10);
      if (!Number.isNaN(batch) && batch > 0) form.append("batch_size", String(batch));
      const { data } = await api.post<AiRun>("/ai-content/runs", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (run) => {
      toast.success(
        `Прогон #${run.id} запущен: ${run.stats.items_total ?? 0} позиций — прогресс в истории ниже`,
      );
      setFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["ai-content-runs"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось запустить прогон"),
  });

  function onFileChange(f: File | null) {
    setFile(f);
    setPreview(null);
    if (f) parseMutation.mutate(f);
  }

  const keySet = Boolean(settings?.anthropic_api_key_set);
  const canStart =
    Boolean(file) &&
    Boolean(preview) &&
    nameCol !== NONE_COLUMN &&
    qtyCol !== NONE_COLUMN &&
    keySet &&
    !startMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-heading flex items-center gap-2">
          <Bot className="w-6 h-6" />
          ИИ-контент — агент карточек товаров
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Загрузите выгрузку остатков из 1С (CSV/XLSX) — агент найдёт товары в
          интернете и создаст карточки-<b>черновики</b> с описаниями на 3
          языках, характеристиками, фото и ценой от конкурентов. Для уже
          существующих товаров обновит остаток и дозаполнит пустые поля.
        </p>
      </div>

      {!keySet && (
        <Card className="border-destructive/40">
          <CardContent className="pt-4 text-sm">
            <span className="text-destructive font-medium">
              Anthropic API ключ не настроен.
            </span>{" "}
            Добавьте его в{" "}
            <Link to="/settings" className="underline">
              Настройки → Общие → Anthropic (Claude)
            </Link>{" "}
            — без ключа запуск недоступен.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Настройки агента</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Модель ИИ</Label>
            <Select
              value={settings?.ai_content_model ?? ""}
              onValueChange={(v) => saveModelMutation.mutate({ ai_content_model: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выбрать модель" />
              </SelectTrigger>
              <SelectContent>
                {(models ?? []).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    <div>
                      <div>{m.label}</div>
                      <div className="text-[10px] text-muted-foreground">{m.price_hint}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <CostCapField
            label="Лимит стоимости прогона, $"
            hint="0 = без лимита; при превышении прогон остановится (можно продолжить)"
            value={settings?.ai_content_cost_cap_usd}
            onSave={(v) => saveModelMutation.mutate({ ai_content_cost_cap_usd: v })}
          />
          <CostCapField
            label="Скидка от цены конкурента, %"
            hint="Стартовая цена новых карточек = цена конкурента минус этот процент"
            value={settings?.ai_content_default_discount_percent}
            onSave={(v) =>
              saveModelMutation.mutate({ ai_content_default_discount_percent: v })
            }
          />
          <CostCapField
            label="Одновременно позиций"
            hint="Сколько карточек агент обрабатывает параллельно. Одна позиция занимает ~4 минуты, так что 5 потоков дают ~70 позиций в час"
            value={settings?.ai_content_concurrency}
            onSave={(v) => saveModelMutation.mutate({ ai_content_concurrency: v })}
          />
          <CostCapField
            label="Наценка без конкурента, %"
            hint="Если товара нет у конкурентов (аксессуары), цена = себестоимость из файла плюс этот процент. 0 — оставлять цену пустой"
            value={settings?.ai_content_markup_percent}
            onSave={(v) => saveModelMutation.mutate({ ai_content_markup_percent: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Файл остатков из 1С
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) onFileChange(dropped);
            }}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
            }`}
          >
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,.txt"
              className="hidden"
              onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            />
            {parseMutation.isPending ? (
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Разбираю файл…
              </p>
            ) : file ? (
              <div className="space-y-1">
                <p className="text-sm font-medium flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-primary" />
                  {file.name}
                </p>
                {preview && (
                  <p className="text-xs text-muted-foreground">
                    {preview.total_rows.toLocaleString("ru-RU")} позиций — нажмите
                    или перетащите другой файл, чтобы заменить
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  Перетащите файл сюда или нажмите, чтобы выбрать
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV или XLSX из 1С — до 5 000 позиций, до 5 МБ
                </p>
              </div>
            )}
          </div>

          {preview && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <ColumnSelect
                  label="Колонка «Код 1С»"
                  headers={preview.headers}
                  value={codeCol}
                  onChange={setCodeCol}
                />
                <ColumnSelect
                  label="Колонка «Название»"
                  headers={preview.headers}
                  value={nameCol}
                  onChange={setNameCol}
                  required
                />
                <ColumnSelect
                  label="Колонка «Остаток»"
                  headers={preview.headers}
                  value={qtyCol}
                  onChange={setQtyCol}
                  required
                />
                <ColumnSelect
                  label="Колонка «Себестоимость» (для автоцены)"
                  headers={preview.headers}
                  value={costCol}
                  onChange={setCostCol}
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Всего позиций в файле: <b>{preview.total_rows.toLocaleString("ru-RU")}</b>{" "}
                — ниже показаны первые {preview.rows.length} строк для проверки колонок.
              </p>
              {codeCol === NONE_COLUMN && (
                <p className="text-xs text-amber-600">
                  Колонка «Код 1С» не выбрана. Без неё агент сопоставляет товары
                  только по названию (менее точно), а будущая интеграция с 1С не
                  сможет связать карточки с номенклатурой — выберите колонку с
                  кодом, если она есть в файле.
                </p>
              )}

              <div className="border rounded-md overflow-x-auto max-h-56 overflow-y-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      {preview.headers.map((h, i) => (
                        <th key={i} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">
                          {h || `(кол. ${i + 1})`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, ri) => (
                      <tr key={ri} className="border-t">
                        {preview.headers.map((_, ci) => (
                          <td key={ci} className="px-2 py-1 whitespace-nowrap max-w-[240px] truncate">
                            {row[ci] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div className="space-y-1.5">
                  <Label className="text-xs">Позиций за один запуск</Label>
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    className="max-w-[110px]"
                  />
                  <p className="text-[10px] text-muted-foreground max-w-md">
                    Пусто — агент идёт по всему файлу сам, без нажатий. Число
                    ограничивает прогон (удобно для пробного запуска на 10–20
                    позициях). Ориентир по деньгам: ~$0,16 за позицию, то есть{" "}
                    <b>
                      ≈$
                      {Math.round(
                        (() => {
                          const b = parseInt(batchSize, 10);
                          const n =
                            !Number.isNaN(b) && b > 0
                              ? Math.min(b, preview.total_rows)
                              : preview.total_rows;
                          return n * 0.16;
                        })(),
                      )}
                    </b>{" "}
                    за этот запуск (лимит в настройках — $
                    {settings?.ai_content_cost_cap_usd ?? 300}).
                  </p>
                </div>
                <Button onClick={() => startMutation.mutate()} disabled={!canStart}>
                  {startMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-1" />
                  )}
                  Запустить агента
                  {preview.total_rows > 0 && (
                    <span className="ml-1 opacity-80">
                      (
                      {(() => {
                        const b = parseInt(batchSize, 10);
                        return !Number.isNaN(b) && b > 0
                          ? Math.min(b, preview.total_rows)
                          : preview.total_rows;
                      })()}{" "}
                      из {preview.total_rows})
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">История прогонов</CardTitle>
        </CardHeader>
        <CardContent>
          {runsLoading ? (
            <Skeleton className="h-24" />
          ) : !runs || runs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Прогонов пока не было.
            </p>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => (
                <RunRow key={run.id} run={run} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CostCapField({
  label,
  hint,
  value,
  onSave,
}: {
  label: string;
  hint: string;
  value: number | undefined;
  onSave: (v: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value !== undefined ? String(value) : "");
  const dirty = draft !== null && draft !== String(value ?? "");
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1.5">
        <Input
          type="number"
          min={0}
          value={shown}
          onChange={(e) => setDraft(e.target.value)}
          className="max-w-[110px]"
        />
        {dirty && (
          <Button
            size="icon"
            variant="outline"
            onClick={() => {
              const n = parseInt(shown, 10);
              if (!Number.isNaN(n) && n >= 0) {
                onSave(n);
                setDraft(null);
              }
            }}
          >
            <Save className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function ColumnSelect({
  label,
  headers,
  value,
  onChange,
  required,
}: {
  label: string;
  headers: string[];
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Не выбрано" />
        </SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value={NONE_COLUMN}>— нет —</SelectItem>}
          {headers.map((h, i) => (
            <SelectItem key={i} value={String(i)}>
              {h || `(кол. ${i + 1})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function RunRow({ run }: { run: AiRun }): JSX.Element {
  const queryClient = useQueryClient();
  const stats = run.stats ?? {};
  const isActive = run.status === "pending" || run.status === "running";
  const total = stats.items_total ?? 0;
  const done = stats.items_done ?? 0;

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/ai-content/runs/${run.id}/cancel`);
    },
    onSuccess: () => {
      toast.success("Прогон помечен как отменённый");
      queryClient.invalidateQueries({ queryKey: ["ai-content-runs"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось отменить"),
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/ai-content/runs/${run.id}/resume`);
    },
    onSuccess: () => {
      toast.success("Прогон продолжен");
      queryClient.invalidateQueries({ queryKey: ["ai-content-runs"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось продолжить"),
  });

  const hasPendingLeft = !isActive && total > done;

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Badge variant={RUN_STATUS_VARIANT[run.status]}>
            {RUN_STATUS_LABEL[run.status]}
          </Badge>
          {isActive && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          <Link
            to={`/configuration/ai-content/${run.id}`}
            className="text-xs underline underline-offset-2"
          >
            #{run.id} · {run.file_name ?? "файл"} ·{" "}
            {new Date(run.created_at).toLocaleString("ru-RU")}
          </Link>
        </div>
        <div className="flex items-center gap-1.5">
          {hasPendingLeft && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
            >
              {resumeMutation.isPending ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Play className="w-3 h-3 mr-1" />
              )}
              Продолжить
            </Button>
          )}
          {isActive && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              <XCircle className="w-3 h-3 mr-1" />
              Отменить
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span>
          Обработано: {done}/{total}
        </span>
        <span>Создано: {stats.items_created ?? 0}</span>
        <span>Обновлено: {stats.items_updated ?? 0}</span>
        {(stats.items_needs_review ?? 0) > 0 && (
          <span className="text-amber-600">На решение: {stats.items_needs_review}</span>
        )}
        {(stats.items_needs_price ?? 0) > 0 && (
          <span className="text-amber-600">Без цены: {stats.items_needs_price}</span>
        )}
        {(stats.items_failed ?? 0) > 0 && (
          <span className="text-destructive">Ошибок: {stats.items_failed}</span>
        )}
        {(stats.cost_usd ?? 0) > 0 && <span>≈${Number(stats.cost_usd).toFixed(2)}</span>}
        <span className="text-muted-foreground/70">{run.model}</span>
      </div>
      {Boolean((stats as Record<string, unknown>).batch_paused) && hasPendingLeft && (
        <p className="text-xs text-muted-foreground">
          Партия обработана — нажмите «Продолжить», чтобы взять следующие{" "}
          {stats.batch_size ?? ""} позиций (осталось {total - done}).
        </p>
      )}
      {run.error_log?.cost_cap_hit && (
        <p className="text-xs text-amber-600">
          Остановлено по лимиту стоимости — поднимите лимит в настройках агента и
          нажмите «Продолжить».
        </p>
      )}
      {run.error_log?.timeout && (
        <p className="text-xs text-amber-600">
          Прервано по таймауту (55 минут) — нажмите «Продолжить», обработка
          возобновится с места остановки.
        </p>
      )}
      {run.error_log?.exception && (
        <p className="text-xs text-destructive break-all">
          Ошибка: {run.error_log.exception}
        </p>
      )}
    </div>
  );
}
